/**
 * Single source for all content-safety limits, rules, and restrictions.
 * Change allowed file types and sizes here (or via future API) — all upload UIs
 * use getAcceptAttribute(), getMaxSizeBytes(), getDropzoneAcceptedTypes() from
 * fileValidation.js, so they stay in sync. Text pipeline and validateFile()
 * also read from this config.
 *
 * Future usage:
 *   const config = await getContentSafetyConfig(); // from API or this default
 *   runTextPipeline(value, 'name', config);
 *   validateFile(file, 'image', config);
 */

export const defaultContentSafetyConfig = {
  // ─── Text: character limits (context-specific) ─────────────────────────────
  limits: {
    name: { min: 2, max: 50 },
    businessName: { min: 3, max: 60 },
    location: { min: 5, max: 100 },
    message: { min: 5, max: 1000 },
    jobTitle: { min: 5, max: 60 },
    jobDetails: { min: 5, max: 1000 },
    reviewText: { min: 30, max: 1000 },
    postTitle: { min: 10, max: 80 },
    portfolioTitle: { min: 2, max: 80 },
    postDescription: { min: 50, max: 500 },
    customSubcategory: { min: 10, max: 40 },
    businessBio: { min: 500, max: 800 },
    topServices: { min: 10, max: 1000 },
    supportMessage: { min: 10, max: 2000 },
    supportSubject: { min: 3, max: 200 },
    reportReason: { min: 10, max: 500 },
  },

  // ─── Names: allowed characters (letters, space, hyphen, apostrophe) ───────
  nameAllowedRegex: /^[\p{L}\s\-']+$/u,
  nameBlockedRegex: /[\d\p{S}\p{P}\p{N}]/u, // numbers, symbols, other punctuation

  // ─── Messages/reviews/forms: block emojis and excessive symbols (Step 2) ─
  blockEmoji: true,
  maxSymbolRatio: 0.4, // block if > 40% of non-space chars are symbols/numbers (excluding basic punctuation .,!?'-)
  basicPunctuation: /[\s.,!?'\-]/g, // allowed punctuation when counting "symbols"

  // ─── Reserved / impersonation terms (blocked in names, descriptions, etc.) ─
  reservedTerms: [
    'admin', 'support', 'staff', 'official', 'helpdesk', 'verified',
    'tradesposter', 'trades poster', 'tradesposter official', 'trades poster support',
  ],
  platformNameVariations: ['tradesposter', 'trades poster', 'trades poster pty ltd'],

  // ─── Gibberish / nonsense detection ──────────────────────────────────────
  gibberish: {
    maxRepeatedChars: 3,        // e.g. "fuuuuck" -> block
    maxCapsRatio: 0.8,          // >80% caps = ALL CAPS abuse
    noVowelMinLength: 4,        // words of this length or more must contain a vowel
    maxWordLength: 50,          // single word length
    keyboardSmashPatterns: ['asdf', 'qwerty', 'zxcv', 'hjkl', 'abcde'],
  },

  // ─── Policy: contact / off-platform (blocked in free text) ─────────────────
  contactPatterns: {
    phone: /\+?\d[\d\s\-()]{8,}/,
    email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/,
    url: /https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-z]{2,}(\/[^\s]*)?/i,
  },
  redirectPhrases: [
    'call me', 'whatsapp me', 'text me', 'email me directly', 'contact me directly',
    'cheaper off platform', 'cheaper elsewhere', 'message me on', 'dm me', 'ping me',
    'my number is', 'my email is', 'reach me at', 'hit me up', 'off platform',
  ],

  // ─── Policy: pricing (blocked outside quote context) ──────────────────────
  pricingPatterns: {
    currencySymbols: /[$€£¥₹]|\b(AUD|USD|EUR|GBP)\b/i,
    priceLike: /\d+\s*(dollars?|bucks?|per\s+(hour|hr|job|m2|sqm))/i,
  },
  // Spec: pricing allowed in Quotes, Quote messaging, Private chat (messaging)
  pricingAllowedContexts: ['quote', 'quoteMessage', 'message'],

  // ─── File: images ────────────────────────────────────────────────────────
  file: {
    image: {
      allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/heic'],
      allowedExtensions: ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'],
      maxSizeBytes: 10 * 1024 * 1024, // 10 MB
      blockedMimeTypes: ['image/gif', 'image/svg+xml', 'image/tiff', 'image/bmp'],
    },
    document: {
      allowedMimeTypes: [
        'application/pdf',
        'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
      ],
      allowedExtensions: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'],
      maxSizeBytes: 20 * 1024 * 1024, // 20 MB
      blockedExtensions: ['.exe', '.zip', '.rar', '.html', '.htm', '.js', '.bat', '.cmd', '.sh'],
    },
  },

  // ─── User-facing error messages (can be overridden by API for i18n) ───────
  messages: {
    reservedTerm: 'This name or term is reserved and cannot be used.',
    nameInvalidChars: 'Only letters, spaces, hyphens and apostrophes are allowed.',
    contactNotAllowed: 'Contact details (phone, email or website) cannot be shared here. Use the platform\'s contact request feature.',
    pricingNotAllowed: 'Pricing and quotes are not allowed in this field. Use the quote section instead.',
    redirectPhrase: 'Please do not ask others to contact you outside the platform.',
    gibberish: 'Please use clear, readable text and avoid excessive repetition or random characters.',
    allCaps: 'Please avoid typing in all capital letters.',
    emojiOrSymbols: 'Please remove emojis or excessive symbols.',
    noVowel: 'Please use real words; avoid strings with no vowels.',
    fileTypeNotAllowed: 'This file type is not allowed.',
    fileTooLarge: 'File is too large. Maximum size is {{max}}.',
    tooShort: 'This field is too short.',
    tooLong: 'This field exceeds the maximum length.',
  },
};

/**
 * Returns the current content safety config. Replace this implementation
 * with an API call when backend settings are ready; keep the same shape.
 *
 * Future example:
 *   export async function getContentSafetyConfig() {
 *     try {
 *       const res = await fetch('/api/settings/content-safety');
 *       const data = await res.json();
 *       return { ...defaultContentSafetyConfig, ...data };
 *     } catch {
 *       return defaultContentSafetyConfig;
 *     }
 *   }
 *
 * @returns {Promise<typeof defaultContentSafetyConfig>}
 */
export async function getContentSafetyConfig() {
  return Promise.resolve(defaultContentSafetyConfig);
}

/**
 * Sync getter for use in validators that don't support async (e.g. Yup.test).
 * Use getContentSafetyConfig() when you can (e.g. on load) and pass config in.
 */
export function getDefaultConfig() {
  return defaultContentSafetyConfig;
}
